
/**
 * Detects infinite loops that would crash the server (instant cycles without pauses).
 * 
 * @param {Array} nodes - The current nodes in the flow.
 * @param {Array} edges - The current connections between nodes.
 * @returns {Object} result - { error: boolean, message?: string, node?: Object }
 */
export const validateInfiniteLoops = (nodes, edges) => {
    // 1. Build Adjacency List (Graph)
    const adj = {};
    const nodeMap = {};

    // Initialize adjacency list for all nodes
    nodes.forEach(node => {
        adj[node.id] = [];
        nodeMap[node.id] = node;
    });

    // Add edges from visual connections
    edges.forEach(edge => {
        if (adj[edge.source]) {
            adj[edge.source].push(edge.target);
        }
    });

    // Add implicit edges from 'Jump' nodes
    // AND explicit edges from 'Jump' nodes (if they connect via cable too, though Jump usually doesn't have output handles)
    nodes.forEach(node => {
        if (node.type === 'jump' && node.data?.destinationNodeId) {
            // Jump node logically jumps to destination
            // We traverse to the destination ID
            if (adj[node.id]) {
                adj[node.id].push(node.data.destinationNodeId);
            }
        }
    });

    // 2. Define "Cycle Breakers" - Nodes that pause/stop execution
    // [ANTIGRAVITY] Updated to include 'openai' and 'gemini' as they now halt execution awaiting interaction
    // [ANTIGRAVITY] Updated to include 'jump' because it now handles its own loop logic with a limit.
    const breakers = ['input', 'ticket', 'attendant', 'interval', 'menu', 'openai', 'gemini', 'jump'];

    // Global sets for DFS state
    const visitedInCurrentPath = new Set();
    const visitedGlobally = new Set();

    // DFS Function
    const detectCycle = (nodeId, path) => {
        const node = nodeMap[nodeId];
        if (!node) return null; // Node might be deleted/invalid

        // CYCLE DETECTED: Node is already in the current recursion stack
        if (visitedInCurrentPath.has(nodeId)) {
            return { detected: true, path: [...path, nodeId], loopingNodeId: nodeId };
        }

        // Optimization: Already processed and safe
        if (visitedGlobally.has(nodeId)) return null;

        // SAFE BREAK: Node takes time/input, breaking infinite recursion risk
        if (breakers.includes(node.type)) {
            visitedGlobally.add(nodeId);
            return null;
        }

        // Mark as visiting
        visitedInCurrentPath.add(nodeId);
        path.push(nodeId);

        // Visit neighbors
        const neighbors = adj[nodeId] || [];
        for (const neighborId of neighbors) {
            const result = detectCycle(neighborId, path);
            if (result && result.detected) {
                return result; // Propagate detection up
            }
        }

        // Backtrack
        visitedInCurrentPath.delete(nodeId);
        path.pop();
        visitedGlobally.add(nodeId); // Mark as checked
        return null;
    };

    // Run DFS from every node
    for (const node of nodes) {
        if (!visitedGlobally.has(node.id)) {
            // Ensure path is clean (should be empty anyway due to backtracking)
            visitedInCurrentPath.clear();
            const cycleResult = detectCycle(node.id, []);

            if (cycleResult && cycleResult.detected) {
                // Analyze the cycle to give a helpful error
                const cycleNodeIds = cycleResult.path;
                // Find the actual cycle part (from where the loop starts)
                const loopStartIndex = cycleNodeIds.indexOf(cycleResult.loopingNodeId);
                const actualLoop = cycleNodeIds.slice(loopStartIndex);

                const loopNodes = actualLoop.map(id => nodeMap[id]);
                const jumpNodeInCycle = loopNodes.find(n => n.type === 'jump');

                if (jumpNodeInCycle) {
                    return {
                        error: true,
                        node: jumpNodeInCycle,
                        message: `Error: El Nodo Saltar '${jumpNodeInCycle.data.name || 'Sin Nombre'}' crea un Bucle Infinito (Loop). Debes incluir un Input, Ticket, Intervalo o Inteligencia Artificial en el ciclo para evitar bloqueos.`
                    };
                } else {
                    return {
                        error: true,
                        node: loopNodes[0],
                        message: `Error: Se detectó un bucle infinito (Loop) empezando en '${loopNodes[0]?.data?.name || loopNodes[0]?.type}'. Asegúrate de incluir pausas (Input/Ticket/Intervalo/IA).`
                    };
                }
            }
        }
    }

    return { error: false };
};
